/**
 * RunsOverTimeChart - Area chart showing runs trend over time.
 * Displays total runs with optional success/failure breakdown.
 */
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { chartColors, chartDefaults, formatChartDate, tooltipStyles } from './chartConfig'
import { BarChart3 } from 'lucide-react'

/**
 * Data point for runs over time chart.
 */
export interface RunsOverTimeDataPoint {
  /** Date string (ISO format) */
  date: string
  /** Total number of runs */
  total: number
  /** Number of successful runs */
  successful?: number
  /** Number of failed runs */
  failed?: number
}

export interface RunsOverTimeChartProps {
  /** Array of data points */
  data: RunsOverTimeDataPoint[]
  /** Show success/failure breakdown instead of total */
  showBreakdown?: boolean
  /** Chart height in pixels */
  height?: number
}

/**
 * Area chart displaying runs over time with optional breakdown.
 * 
 * @example
 * ```tsx
 * <RunsOverTimeChart 
 *   data={[
 *     { date: '2025-12-19', total: 10, successful: 8, failed: 2 },
 *     { date: '2025-12-20', total: 15, successful: 12, failed: 3 },
 *   ]} 
 *   showBreakdown 
 * />
 * ```
 */
export function RunsOverTimeChart({
  data,
  showBreakdown = false,
  height = chartDefaults.height,
}: RunsOverTimeChartProps) {
  // Empty state
  if (!data || data.length === 0) {
    return (
      <div 
        className="flex flex-col items-center justify-center text-muted-foreground"
        style={{ height }}
      >
        <BarChart3 className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">No data available</p>
      </div>
    )
  }

  // Format data for display
  const formattedData = data.map(item => ({
    ...item,
    displayDate: formatChartDate(item.date),
  }))

  return (
    <div 
      role="img" 
      aria-label="Runs over time area chart showing run trends"
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <AreaChart 
          data={formattedData} 
          margin={chartDefaults.margin}
        >
          <defs>
            <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColors.info} stopOpacity={0.3} />
              <stop offset="95%" stopColor={chartColors.info} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColors.success} stopOpacity={0.3} />
              <stop offset="95%" stopColor={chartColors.success} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="failedGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColors.error} stopOpacity={0.3} />
              <stop offset="95%" stopColor={chartColors.error} stopOpacity={0} />
            </linearGradient>
          </defs>
          
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={chartColors.grid}
            opacity={0.5}
          />
          
          <XAxis 
            dataKey="displayDate" 
            tick={{ fontSize: 12, fill: chartColors.muted }}
            tickLine={false}
            axisLine={{ stroke: chartColors.grid }}
          />
          
          <YAxis 
            tick={{ fontSize: 12, fill: chartColors.muted }}
            tickLine={false}
            axisLine={{ stroke: chartColors.grid }}
            allowDecimals={false}
          />
          
          <Tooltip 
            contentStyle={tooltipStyles.contentStyle}
            labelStyle={tooltipStyles.labelStyle}
            itemStyle={tooltipStyles.itemStyle}
            labelFormatter={(label) => `Date: ${label}`}
          />
          
          {showBreakdown ? (
            <>
              <Legend 
                verticalAlign="top" 
                height={36}
                iconType="circle"
              />
              <Area
                type="monotone"
                dataKey="failed"
                name="Failed"
                stroke={chartColors.error}
                fill="url(#failedGradient)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="successful"
                name="Successful"
                stroke={chartColors.success}
                fill="url(#successGradient)"
                strokeWidth={2}
              />
            </>
          ) : (
            <Area
              type="monotone"
              dataKey="total"
              name="Total Runs"
              stroke={chartColors.info}
              fill="url(#totalGradient)"
              strokeWidth={2}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
